import clsx from 'clsx';
import React, { type MouseEvent } from 'react';

import { PrimaryButton } from '@components/Button/variants';
import Spinner from '@components/Spinner';
import { useAppContext } from '@contexts/AppContext';
import { useBlueconicContext } from '@contexts/BlueconicContext';
import useArticlesBySlug from '@hooks/useArticlesBySlug';
import useEventTracking from '@hooks/useEventTracking';
import { ClickTarget, click } from '@tracking';
import { captureBlueconicClickEvent } from '@utils/blueconic/utils';

import { LeadCaptureModalBodyProps } from '../../LeadCaptureModalBody';
import LeadCaptureModalBody from '../../LeadCaptureModalBody';
import { ModalHeaderProps } from '../../common/modalHeader';
import ArticleItem from './ArticleItem';

export type LeadCaptureModalWithHorizontalArticlesProps = LeadCaptureModalBodyProps &
  ModalHeaderProps;

const LeadCaptureModalWithHorizontalArticles = ({
  articleSlugs,
  leadType,
  title,
  subtitle,
  buttonText,
  offerLink,
  offerCode,
}: LeadCaptureModalWithHorizontalArticlesProps) => {
  const { trackEvent } = useEventTracking();
  const { variantId } = useBlueconicContext();

  const { data: articles, isLoading } = useArticlesBySlug({ articleSlugs });
  const { initialCurrentUser } = useAppContext();
  const isCurrentUserPresented = !!initialCurrentUser;

  const handleEventClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    trackEvent(click({ clickTarget: ClickTarget.Button, clickText: buttonText }));
    if (variantId) {
      captureBlueconicClickEvent(variantId);
    }
  };

  return (
    <div className="flex w-full flex-col">
      <div className="px-24 py-32 sm:px-64 md:py-40">
        <div className="mb-16 font-suisse-works text-headline-xs text-primary-magenta md:text-headline-md lg:text-headline-lg">
          {title}
        </div>
        {isLoading ? (
          <Spinner />
        ) : (
          <div className="flex flex-wrap sm:gap-24 md:flex-nowrap lg:max-w-[900px] lg:gap-64">
            {articles.map((article) => (
              <ArticleItem key={article.id} article={article} offerCode={offerCode} />
            ))}
          </div>
        )}
      </div>
      <div
        className={clsx(
          `mt-auto flex flex-wrap  items-center gap-24 bg-grayscale-charcoal px-24 py-20 text-white sm:px-64 md:px-0 lg:py-32`,
          {
            'justify-start md:justify-center': isCurrentUserPresented,
            'justify-center': !isCurrentUserPresented,
          }
        )}
      >
        <div
          className={clsx(`text-center font-suisse-screen text-headline-xxs md:px-0`, {
            'sm:text-left': isCurrentUserPresented,
            'w-full': !isCurrentUserPresented,
          })}
        >
          {subtitle}
        </div>
        {isCurrentUserPresented ? (
          <div className="flex w-full justify-center sm:static sm:w-auto">
            <PrimaryButton
              as="link"
              href={offerLink}
              className="max-w-[160px] sm:w-auto"
              onClickCapture={handleEventClick}
            >
              {buttonText}
            </PrimaryButton>
          </div>
        ) : (
          <div className="w-full sm:w-[370px]">
            <LeadCaptureModalBody
              buttonText={buttonText}
              leadType={leadType}
              offerLink={offerLink}
              withOauth={false}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default LeadCaptureModalWithHorizontalArticles;
